import { RailArrow, edgeFadeMask } from "@/components/home/SectionRail";
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";

type NodeRef =
  | ((node: HTMLDivElement | null) => void)
  | { current: HTMLDivElement | null };

function assignRef(ref: NodeRef | undefined, value: HTMLDivElement | null) {
  if (!ref) return;
  if (typeof ref === "function") {
    ref(value);
    return;
  }
  ref.current = value;
}

/**
 * Horizontal scroller of the storefront rails: hidden scrollbar, soft edge
 * fade, and the prev/next arrows. `viewport` advances most of the visible
 * width; `card` advances one slide.
 */
export function RailScroller({
  children,
  className,
  scrollerClassName,
  rootRef,
  scrollerRef,
  watch,
  step = "viewport",
  marker,
}: {
  children: ReactNode;
  className?: string;
  scrollerClassName?: string;
  rootRef?: NodeRef;
  scrollerRef?: NodeRef;
  watch?: unknown;
  step?: "viewport" | "card";
  marker?: string;
}) {
  const innerRef = useRef<HTMLDivElement | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const setRoot = useCallback(
    (node: HTMLDivElement | null) => {
      assignRef(rootRef, node);
    },
    [rootRef],
  );

  const setScroller = useCallback(
    (node: HTMLDivElement | null) => {
      innerRef.current = node;
      assignRef(scrollerRef, node);
    },
    [scrollerRef],
  );

  useEffect(() => {
    const scroller = innerRef.current;
    if (!scroller) return;

    const updateEdges = () => {
      setCanScrollLeft(scroller.scrollLeft > 2);
      setCanScrollRight(
        scroller.scrollLeft + scroller.clientWidth < scroller.scrollWidth - 2,
      );
    };

    updateEdges();
    scroller.addEventListener("scroll", updateEdges, { passive: true });
    const observer = new ResizeObserver(updateEdges);
    observer.observe(scroller);
    return () => {
      scroller.removeEventListener("scroll", updateEdges);
      observer.disconnect();
    };
  }, [watch]);

  function scrollByDirection(direction: -1 | 1) {
    const scroller = innerRef.current;
    if (!scroller) return;
    const first =
      step === "card"
        ? scroller.querySelector<HTMLElement>(":scope > ul > li")
        : null;
    const pitch = first ? first.offsetWidth : Math.round(scroller.clientWidth * 0.9);
    scroller.scrollBy({ left: direction * pitch, behavior: "smooth" });
  }

  const fade = edgeFadeMask({ left: canScrollLeft, right: canScrollRight });

  return (
    <div ref={setRoot} className={className ? `relative ${className}` : "relative"}>
      <div
        ref={setScroller}
        data-rail-scroller=""
        {...(marker ? { [`data-${marker}`]: "" } : {})}
        className={["overflow-x-auto snap-x [&::-webkit-scrollbar]:hidden", scrollerClassName]
          .filter(Boolean)
          .join(" ")}
        style={{
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          WebkitMaskImage: fade,
          maskImage: fade,
        }}
      >
        {children}
      </div>
      {canScrollLeft && <RailArrow direction="prev" onClick={() => scrollByDirection(-1)} />}
      {canScrollRight && <RailArrow direction="next" onClick={() => scrollByDirection(1)} />}
    </div>
  );
}
